import { useCallback, useRef, useEffect } from 'react';

type Task = () => Promise<void>;

export function useTaskQueue() {
  const tasksRef = useRef<Task[]>([]);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    return () => {
      tasksRef.current = [];
      isProcessingRef.current = false;
    };
  }, []);

  const processNextTask = useCallback(() => {
    if (isProcessingRef.current || tasksRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    const nextTask = tasksRef.current.shift();
    if (!nextTask) {
      isProcessingRef.current = false;
      return;
    }
    nextTask()
      .catch(() => {
        console.log('Task failed');
      })
      .finally(() => {
        isProcessingRef.current = false;
        if (tasksRef.current.length > 0) {
          processNextTask();
        }
      });
  }, []);

  const addTask = useCallback(
    (task: Task) => {
      tasksRef.current.push(task);
      if (!isProcessingRef.current) {
        processNextTask();
      }
    },
    [processNextTask]
  );

  return {
    addTask,
  };
}
